import React, { Fragment, useEffect, useRef, useState } from 'react';
import { translate } from '../Services/translation';
import { useDispatch, useSelector } from 'react-redux';
import InputField from './InputField';
import RegisterLicenseCodeModal from './RegisterLicenseCodeModal';
import { post } from '../Services/http';
import { catchError } from '../Actions/Error.action';
import { error } from '../Reducers/Error.reducer';
import { handleApiErrors } from '../Services/Error.service';
import { 
    clearError, 
    loadError, 
    loadValidationSuccess,
    resetModalState,
} from '../Actions/RegisterLicenseCode.action';
import { registerLicenseCodeSchema } from './RegisterLicenseCode.ValidationSchema';
import { getUrlParamsLowerCase } from '../Services/UrlUtilities.service';

const validateInput = async (licenseCode) => {
    return await registerLicenseCodeSchema.validate({ licenseCode: licenseCode });
};

const RegisterLicenseCode = () => {
    const urlParams = getUrlParamsLowerCase(window.location.search);
    const licenseCodeFromQuery = urlParams?.get("licensecode");
    const redirectUrl = urlParams?.get("redirecturl");

    const dispatch = useDispatch();
    const { payload, errors, step } = useSelector((state) => state.registerLicenseCode);

    const dialogRef = useRef(null);
    const [showModal, setShowModal] = useState(false);
    const [licenseCode, setLicenseCode] = useState(licenseCodeFromQuery ?? '');
    const [formVisible, setFormVisible] = useState(Boolean(licenseCode));

    useEffect(() => {
        if (!dialogRef) return;
        if (showModal && !dialogRef.current?.open) dialogRef.current.showModal();
        if (!showModal && dialogRef.current?.open) dialogRef.current.close();
    }, [showModal]);

    useEffect(() => {
        if (step === 'modalError') setShowModal(true);
        if (step === 'validationSuccess') setShowModal(true);
    }, [step]);

    useEffect(() => {
        const submit = async() => {
            if (licenseCodeFromQuery && licenseCode) {
                await lookUpCode();
            }
        }
        submit();
    }, [licenseCodeFromQuery]);
  
    const handleSubmit = async (e) => {
        e.preventDefault();
        await lookUpCode();
    }

    const lookUpCode = async() => {
        try {
            await validateInput(licenseCode)
            const response = await post('/api/products/licenseCode/lookup', { licenseCode });
            const data = await response.json();
            dispatch(loadValidationSuccess(data));
        } catch (ex) {
            await dispatch(catchError(ex, (error) => handleApiErrors(error, loadError)));
        }
    }

    const handleCloseModal = () => {
        dispatch(resetModalState());
        setShowModal(false);
        enableBodyScroll();
    }

    const handleToggleForm = () => {
        if (formVisible) dispatch(clearError());
        setFormVisible(prev => !prev)
    }

    const handleChange = (value) => {
        const raw = value.replace(/-/g, "")?.slice(0,16);
        setLicenseCode(raw?.match(/.{1,4}/g)?.join("-") ?? "")
    }

    return (
        <Fragment>
            <div className="register-license-code">
            <div onClick={handleToggleForm} className="register-license-code__head">
                {formVisible ?
                    (<i className="icon-cancel_circle register-license-code__head__icon__close"></i>)
                :
                    (<i className="icon-plus_circle register-license-code__head__icon__open"></i>)
                }
                <span>{translate('js.registerlicensecode.heading.register')}</span>
            </div>
            {formVisible && (
                <div className="register-license-code__content">
                    <p>{translate('js.registerlicensecode.text')}</p>
                        <form onSubmit={handleSubmit}>
                            <InputField
                                id='licenseCode'
                                name='licenseCode'
                                type='text'
                                value={licenseCode}
                                required={true}
                                onChange={handleChange}
                                errors={errors}
                                placeholder={translate('js.registerlicensecode.input.placeholdertext')}
                            />
                            <button className="button button-primary">
                                {translate('js.registerlicensecode.button.confirm.text')}
                            </button>
                        </form>
                </div>)
            }
            </div>
            {showModal && (
                <dialog ref={dialogRef} className="dialog">
                    <RegisterLicenseCodeModal 
                        acceptTermsCheckboxText={payload?.acceptTermsCheckboxText} 
                        acceptTermsText={payload?.acceptTermsText}
                        errors={errors} 
                        handleClose={handleCloseModal}
                        licenseCode={licenseCode}
                        productImageUrl={payload?.productImageUrl ?? errors.productImageUrl}
                        productName={payload?.productName ?? errors.productName}
                        redirectUrl={redirectUrl}
                    />
                </dialog>
            )}
        </Fragment>
    )
}

export default RegisterLicenseCode;